// ═══════════════════════════════════════════════════════════════
//  轻量 i18n
//  - 每种语言一个 JSON（src/i18n/<code>.json）
//  - 启动时按系统语言自动选择；无对应语言 / 地区则回退英文
//  - 用户选择会存 localStorage，下次优先生效
// ═══════════════════════════════════════════════════════════════
import en from './en.json';
import zh from './zh.json';
import zhHant from './zh-Hant.json';
import ja from './ja.json';

export interface LanguageInfo {
  code: string;
  label: string;
}

/** 可选语言（顺序即菜单中的显示顺序） */
export const LANGUAGES: LanguageInfo[] = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '简体中文' },
  { code: 'zh-Hant', label: '繁體中文' },
  { code: 'ja', label: '日本語' },
];

const BUNDLES: Record<string, any> = {
  en,
  zh,
  'zh-Hant': zhHant,
  ja,
};

const FALLBACK = 'en';
const STORAGE_KEY = 'dungeon.lang';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 忽略（隐私模式等） */
  }
}

/** 把任意地区码规范化成我们支持的语言码；不支持则返回 null */
export function normalizeLanguage(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = String(raw).replace(/_/g, '-');
  const lower = code.toLowerCase();

  // 1) 完全匹配
  const exact = LANGUAGES.find((l) => l.code.toLowerCase() === lower);
  if (exact) return exact.code;

  const base = lower.split('-')[0];

  // 2) 中文按地区区分简繁（无地区默认简体）
  if (base === 'zh') {
    if (/(hant|tw|hk|mo)/.test(lower)) return 'zh-Hant';
    return 'zh';
  }

  // 3) 按主语言匹配
  const byBase = LANGUAGES.find((l) => l.code.toLowerCase().split('-')[0] === base);
  return byBase ? byBase.code : null;
}

/** 自动选择语言：本地保存 > 系统语言 > 英文 */
export function detectLanguage(): string {
  const stored = safeGet(STORAGE_KEY);
  if (stored && BUNDLES[stored]) return stored;

  const nav: string[] =
    typeof navigator !== 'undefined'
      ? (navigator.languages && navigator.languages.length
          ? Array.from(navigator.languages)
          : [navigator.language])
      : [];

  for (const l of nav) {
    const n = normalizeLanguage(l);
    if (n) return n;
  }
  return FALLBACK;
}

let current: string = detectLanguage();

export function getLanguage(): string {
  return current;
}

export function setLanguage(code: string): void {
  if (BUNDLES[code]) {
    current = code;
    safeSet(STORAGE_KEY, code);
  }
}

function lookup(bundle: any, key: string): string | undefined {
  const v = key.split('.').reduce<any>((o, k) => (o == null ? undefined : o[k]), bundle);
  return typeof v === 'string' ? v : undefined;
}

/**
 * 翻译。支持 {name} 占位符；缺失的键回退到英文，再回退到键名本身。
 * @param params 占位符参数
 */
export function tr(key: string, params?: Record<string, string | number>): string {
  let s = lookup(BUNDLES[current], key);
  if (s === undefined) s = lookup(BUNDLES[FALLBACK], key);
  if (s === undefined) return key;
  if (params) {
    s = s.replace(/\{(\w+)\}/g, (_m, k: string) =>
      params[k] !== undefined && params[k] !== null ? String(params[k]) : `{${k}}`
    );
  }
  return s;
}
